/**
 * Contract Data Service
 * Handles fetching contract information from the backend API
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

/**
 * Get contracts for specific tract numbers
 */
export async function getContractsByTractNumbers(tractNumbers) {
  try {
    if (!tractNumbers || tractNumbers.length === 0) {
      return [];
    }

    const tractNumbersParam = tractNumbers.join(',');
    const response = await fetch(
      `${BACKEND_URL}/api/contracts?tractNumbers=${tractNumbersParam}`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.success ? data.data : [];
  } catch (error) {
    console.error('Failed to fetch contracts by tract numbers:', error);
    return [];
  }
}

/**
 * Get contracts by operator name
 */
export async function getContractsByOperator(operator) {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/contracts/operator/${encodeURIComponent(operator)}`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.success ? data.data : [];
  } catch (error) {
    console.error('Failed to fetch contracts by operator:', error);
    return [];
  }
}

/**
 * Get expiring contracts (within specified days)
 */
export async function getExpiringContracts(days = 30) {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/contracts/expiring/${days}`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.success ? data.data : [];
  } catch (error) {
    console.error('Failed to fetch expiring contracts:', error);
    return [];
  }
}

/**
 * Get all contracts with optional filters
 */
export async function getAllContracts(filters = {}) {
  try {
    const params = new URLSearchParams();

    if (filters.operator) params.append('operator', filters.operator);
    if (filters.status) params.append('status', filters.status);
    if (filters.contractType) params.append('contractType', filters.contractType);
    if (filters.minAcres) params.append('minAcres', filters.minAcres);
    if (filters.tractNumbers) params.append('tractNumbers', filters.tractNumbers);

    const response = await fetch(
      `${BACKEND_URL}/api/contracts?${params.toString()}`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.success ? data.data : [];
  } catch (error) {
    console.error('Failed to fetch contracts:', error);
    return [];
  }
}

/**
 * Get contract statistics
 */
export async function getContractStats() {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/contracts/stats/summary`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.success ? data.data : null;
  } catch (error) {
    console.error('Failed to fetch contract stats:', error);
    return null;
  }
}

/**
 * Get operator summary
 */
export async function getOperatorSummary() {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/contracts/stats/operators`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.success ? data.data : [];
  } catch (error) {
    console.error('Failed to fetch operator summary:', error);
    return [];
  }
}

/**
 * Correlate GIS features with contract data
 * Returns enriched summary with contract information
 */
export async function enrichWithContractData(gisFeatures) {
  try {
    // Extract tract numbers from GIS features (check multiple field name variations)
    const tractNumbers = gisFeatures
      .map(feature =>
        feature.attributes?.TRACT_NUMBER ||
        feature.attributes?.TractNumber ||
        feature.attributes?.tract_number ||
        feature.properties?.TRACT_NUMBER ||
        feature.properties?.TractNumber
      )
      .filter(Boolean);

    if (tractNumbers.length === 0) {
      return { contracts: [], contractStats: null };
    }

    // Fetch contracts for these tracts
    const contracts = await getContractsByTractNumbers(tractNumbers);

    // Calculate contract statistics
    const contractStats = {
      totalContracts: contracts.length,
      activeContracts: contracts.filter(c => c.lease_status === 'Active').length,
      expiredContracts: contracts.filter(c => c.lease_status === 'Expired').length,
      totalMonthlyRevenue: contracts.reduce((sum, c) => sum + (c.monthly_revenue || 0), 0),
      averageRoyaltyPercentage: contracts.length > 0
        ? contracts.reduce((sum, c) => sum + (c.royalty_percentage || 0), 0) / contracts.length
        : 0,
      contractsByOperator: contracts.reduce((acc, c) => {
        acc[c.operator_name] = (acc[c.operator_name] || 0) + 1;
        return acc;
      }, {}),
      contractsByType: contracts.reduce((acc, c) => {
        acc[c.contract_type] = (acc[c.contract_type] || 0) + 1;
        return acc;
      }, {}),
      revenueByOperator: contracts.reduce((acc, c) => {
        acc[c.operator_name] = (acc[c.operator_name] || 0) + (c.monthly_revenue || 0);
        return acc;
      }, {})
    };

    return { contracts, contractStats };
  } catch (error) {
    console.error('Failed to enrich with contract data:', error);
    return { contracts: [], contractStats: null };
  }
}

export default {
  getContractsByTractNumbers,
  getContractsByOperator,
  getExpiringContracts,
  getAllContracts,
  getContractStats,
  getOperatorSummary,
  enrichWithContractData
};
